<script>
  import { onMount } from "svelte";
  import Icon from "./Icon.svelte";

  /**
   * @typedef {'success' | 'error' | 'warning' | 'info'} ToastType
   * @typedef {{ id: string, type: ToastType, message: string, title?: string, duration?: number }} ToastItem
   */

  /** @type {{ toast: ToastItem, onDismiss: (id: string) => void }} */
  let { toast, onDismiss } = $props();

  let visible = $state(false);
  let exiting = $state(false);

  const iconMap = {
    success: "checkCircle",
    error: "alertCircle",
    warning: "alertCircle",
    info: "info",
  };

  onMount(() => {
    // Trigger entrance animation on next frame
    const enterFrame = requestAnimationFrame(() => {
      visible = true;
    });

    // Auto-dismiss after duration
    const duration = toast.duration || 3000;
    const dismissTimer = setTimeout(() => {
      exiting = true;
      // Remove after exit animation completes
      setTimeout(() => {
        onDismiss(toast.id);
      }, 300);
    }, duration);

    return () => {
      cancelAnimationFrame(enterFrame);
      clearTimeout(dismissTimer);
    };
  });
</script>

<div
  class="toast toast-{toast.type}"
  class:visible
  class:exiting
  role="alert"
  aria-live="polite"
>
  <div class="toast-inner">
    <div class="toast-icon">
      <Icon name={iconMap[toast.type]} size={18} />
    </div>
    <div class="toast-content">
      {#if toast.title}
        <span class="toast-title">{toast.title}</span>
      {/if}
      <span class="toast-message">{toast.message}</span>
    </div>
  </div>
  <div
    class="toast-progress"
    style="animation-duration: {toast.duration || 3000}ms"
  ></div>
</div>

<style>
  .toast {
    width: 100%;
    position: relative;
    transform: translateY(-100%);
    opacity: 0;
    transition: all 0.35s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .toast.visible {
    transform: translateY(0);
    opacity: 1;
  }

  .toast.exiting {
    transform: translateY(-100%);
    opacity: 0;
    transition: all 0.3s cubic-bezier(0.4, 0, 1, 1);
  }

  .toast-inner {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 0.875rem 1rem;
    width: 100%;
  }

  /* Success - green */
  .toast-success {
    background: linear-gradient(135deg, #059669 0%, #10b981 100%);
    color: white;
  }

  /* Error - red */
  .toast-error {
    background: linear-gradient(135deg, #dc2626 0%, #ef4444 100%);
    color: white;
  }

  /* Warning - amber */
  .toast-warning {
    background: linear-gradient(135deg, #d97706 0%, #f59e0b 100%);
    color: white;
  }

  /* Info - blue */
  .toast-info {
    background: linear-gradient(135deg, #2563eb 0%, #3b82f6 100%);
    color: white;
  }

  .toast-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    opacity: 0.9;
  }

  .toast-content {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9375rem;
    font-weight: 500;
    text-align: center;
  }

  .toast-title {
    font-weight: 600;
  }

  .toast-title::after {
    content: " — ";
    opacity: 0.6;
  }

  .toast-message {
    opacity: 0.95;
  }

  .toast-progress {
    position: absolute;
    bottom: 0;
    left: 0;
    height: 3px;
    background: rgba(255, 255, 255, 0.4);
    animation: shrink linear forwards;
  }

  @keyframes shrink {
    from {
      width: 100%;
    }
    to {
      width: 0%;
    }
  }
</style>
